import React, { useState, useEffect, useCallback } from 'react';
import { medicationHistoryService, MedicationHistoryImpact, MedicationHistoryConclusion } from '../lib/medicationHistoryService';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { Skeleton, SkeletonList } from './common/Skeleton';

interface SupplementHistoryViewProps {
  userId: string;
}

const rem = (px: number) => `${px / 16}rem`;

const fontSize = {
  xs: rem(12),
  sm: rem(14),
  base: rem(16),
  lg: rem(18),
  xl: rem(20),
  '2xl': rem(24)
};

export const SupplementHistoryView: React.FC<SupplementHistoryViewProps> = ({ userId }) => {
  const [impacts, setImpacts] = useState<MedicationHistoryImpact[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSupplementId, setSelectedSupplementId] = useState<string | null>(null);
  const [conclusionFilter, setConclusionFilter] = useState<MedicationHistoryConclusion | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const data = await medicationHistoryService.analyzeSupplementHistory(userId);
      setImpacts(data);
    } catch (error) {
      console.error('Błąd ładowania historii suplementów:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const getConclusionLabel = (conclusion: MedicationHistoryConclusion): string => {
    switch (conclusion) {
      case 'improvement':
        return 'Obserwowano poprawę';
      case 'no_change':
        return 'Brak wyraźnej zmiany';
      case 'worsening':
        return 'Obserwowano pogorszenie';
      case 'insufficient_data':
        return 'Brak danych do oceny';
    }
  };

  const getConclusionColor = (conclusion: MedicationHistoryConclusion): string => {
    switch (conclusion) {
      case 'improvement':
        return '#00A86B'; // Zielony
      case 'no_change':
        return '#999999'; // Szary
      case 'worsening':
        return '#FF4444'; // Czerwony
      case 'insufficient_data':
        return '#FFA500'; // Pomarańczowy
    }
  };

  const filteredImpacts = (conclusionFilter === 'all' 
    ? impacts 
    : impacts.filter(impact => impact.conclusion === conclusionFilter))
    .filter(impact => 
      searchTerm === '' || 
      impact.medicationName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const selectedImpact = selectedSupplementId 
    ? impacts.find(i => i.medicationId === selectedSupplementId)
    : null;

  if (loading) {
    return (
      <div style={{ padding: rem(24) }}>
        <Skeleton width="60%" height="24px" borderRadius="8px" />
        <div style={{ height: rem(16) }} />
        <SkeletonList count={4} />
      </div>
    );
  }

  if (impacts.length === 0) {
    return (
      <div style={{ padding: rem(24), textAlign: 'center' }}>
        <p style={{ color: '#999999', fontSize: fontSize.base }}>
          Brak suplementów do analizy. Dodaj suplementy, aby zobaczyć historię obserwacji.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: rem(24) }}>
      {/* Disclaimer */}
      <div style={{
        padding: rem(16),
        background: '#FFF5E6',
        border: '1px solid #FFE0B2',
        borderRadius: rem(12),
        marginBottom: rem(24)
      }}>
        <p style={{
          fontSize: fontSize.sm,
          color: '#666666',
          margin: 0,
          lineHeight: 1.5,
          fontWeight: 600,
          marginBottom: rem(8)
        }}>
          Suplementy nie są leczeniem IBD.
        </p>
        <p style={{
          fontSize: fontSize.sm,
          color: '#666666',
          margin: 0,
          lineHeight: 1.5
        }}>
          Ta analiza pokazuje obserwacje z Twoich danych i możliwe powiązania, nie przyczyny.
        </p>
      </div>

      {/* Wyszukiwarka */}
      <div style={{ marginBottom: rem(16) }}>
        <input
          type="text"
          placeholder="Wyszukaj suplement po nazwie..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: `${rem(10)} ${rem(16)}`,
            borderRadius: rem(8),
            border: '1px solid #E5E5E5',
            fontSize: fontSize.sm,
            color: '#1A1A1A'
          }}
        />
      </div>

      {/* Filtry */}
      <div style={{
        display: 'flex',
        gap: rem(8),
        flexWrap: 'wrap',
        marginBottom: rem(24),
        padding: rem(4),
        background: '#F5F5F5',
        borderRadius: rem(12),
        border: '1px solid #E5E5E5'
      }}>
        <button
          onClick={() => setConclusionFilter('all')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'all' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'all' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'all' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'all' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Wszystkie ({impacts.length})
        </button>
        <button
          onClick={() => setConclusionFilter('improvement')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'improvement' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'improvement' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'improvement' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'improvement' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Poprawa ({impacts.filter(i => i.conclusion === 'improvement').length})
        </button>
        <button
          onClick={() => setConclusionFilter('no_change')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'no_change' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'no_change' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'no_change' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'no_change' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Brak zmiany ({impacts.filter(i => i.conclusion === 'no_change').length})
        </button>
        <button
          onClick={() => setConclusionFilter('worsening')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'worsening' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'worsening' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'worsening' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'worsening' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Pogorszenie ({impacts.filter(i => i.conclusion === 'worsening').length})
        </button>
        <button
          onClick={() => setConclusionFilter('insufficient_data')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'insufficient_data' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'insufficient_data' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'insufficient_data' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'insufficient_data' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Brak danych ({impacts.filter(i => i.conclusion === 'insufficient_data').length})
        </button>
      </div>

      {/* Widok szczegółowy */}
      {selectedImpact && (
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(24),
          marginBottom: rem(24)
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: rem(20)
          }}>
            <div>
              <h2 style={{
                fontSize: fontSize.lg,
                fontWeight: 600,
                color: '#1A1A1A',
                margin: 0,
                marginBottom: rem(8)
              }}>
                {selectedImpact.medicationName}
              </h2>
              <div style={{
                fontSize: fontSize.sm,
                color: '#666666',
                marginBottom: rem(4)
              }}>
                Okres stosowania: {format(parseISO(selectedImpact.startDate), 'dd.MM.yyyy', { locale: pl })}
                {selectedImpact.endDate && ` — ${format(parseISO(selectedImpact.endDate), 'dd.MM.yyyy', { locale: pl })}`}
                {!selectedImpact.endDate && ' — obecnie'}
              </div>
              <div style={{
                fontSize: fontSize.sm,
                color: '#666666'
              }}>
                Status: {selectedImpact.status === 'active' ? 'Aktywny' : 'Zakończony'}
              </div>
            </div>
            <button
              onClick={() => setSelectedSupplementId(null)}
              style={{
                padding: rem(8),
                background: 'transparent',
                border: '1px solid #E5E5E5',
                borderRadius: rem(8),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                color: '#666666'
              }}
            >
              ✕
            </button>
          </div>

          {selectedImpact.hasEnoughData ? (
            <>
              {/* Porównanie przed/po */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: rem(16),
                marginBottom: rem(24)
              }}>
                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Dni zaostrzeń
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.daysWithFlare}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.daysWithFlare}</strong>
                  </div>
                  {selectedImpact.change.flareDaysChange !== 0 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.flareDaysChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.flareDaysChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.flareDaysChange)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średnia liczba wypróżnień
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgBmPerDay.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgBmPerDay.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgBmChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgBmChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgBmChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.avgBmChange).toFixed(1)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Dni z krwią
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.bloodDaysCount}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.bloodDaysCount}</strong>
                  </div>
                  {selectedImpact.change.bloodDaysChange !== 0 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.bloodDaysChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.bloodDaysChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.bloodDaysChange)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średni ból
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgPain.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgPain.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgPainChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgPainChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgPainChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.avgPainChange).toFixed(1)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średnia pilność
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgUrgency.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgUrgency.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgUrgencyChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgUrgencyChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgUrgencyChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.avgUrgencyChange).toFixed(1)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średni poziom energii
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgEnergy.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgEnergy.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgEnergyChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgEnergyChange > 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgEnergyChange > 0 ? '↑' : '↓'} {Math.abs(selectedImpact.change.avgEnergyChange).toFixed(1)}
                    </div>
                  )}
                </div>
              </div>

              {/* Podsumowanie obserwacyjne */}
              <div style={{
                padding: rem(16),
                background: '#FFF5E6',
                borderRadius: rem(8),
                border: `2px solid ${getConclusionColor(selectedImpact.conclusion)}`,
                marginBottom: rem(16)
              }}>
                <div style={{
                  fontSize: fontSize.base,
                  fontWeight: 600,
                  color: getConclusionColor(selectedImpact.conclusion),
                  marginBottom: rem(8)
                }}>
                  {getConclusionLabel(selectedImpact.conclusion)}
                </div>
                <div style={{
                  fontSize: fontSize.sm,
                  color: '#666666',
                  lineHeight: 1.5
                }}>
                  {selectedImpact.summary}
                </div>
              </div>

              {/* Informacja o okresie analizy */}
              <div style={{
                fontSize: fontSize.xs,
                color: '#999999',
                fontStyle: 'italic'
              }}>
                Analiza obejmuje okres przed rozpoczęciem suplementu ({format(parseISO(selectedImpact.beforePeriod.periodStart), 'dd.MM.yyyy', { locale: pl })} — {format(parseISO(selectedImpact.beforePeriod.periodEnd), 'dd.MM.yyyy', { locale: pl })})
                {' '}oraz okres po rozpoczęciu ({format(parseISO(selectedImpact.afterPeriod.periodStart), 'dd.MM.yyyy', { locale: pl })} — {format(parseISO(selectedImpact.afterPeriod.periodEnd), 'dd.MM.yyyy', { locale: pl })}).
              </div>
            </>
          ) : (
            <div style={{
              padding: rem(16),
              background: '#FFF5E6',
              borderRadius: rem(8),
              border: '1px solid #FFE0B2'
            }}>
              <p style={{
                fontSize: fontSize.sm,
                color: '#666666',
                margin: 0
              }}>
                {selectedImpact.summary}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Lista suplementów */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: rem(16)
      }}>
        {filteredImpacts.map((impact) => (
          <div
            key={impact.medicationId}
            onClick={() => setSelectedSupplementId(impact.medicationId)}
            style={{
              padding: rem(20),
              background: '#FFFFFF',
              border: `2px solid ${getConclusionColor(impact.conclusion)}`,
              borderRadius: rem(12),
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <h3 style={{
              fontSize: fontSize.base,
              fontWeight: 600,
              color: '#1A1A1A',
              margin: 0,
              marginBottom: rem(8)
            }}>
              {impact.medicationName}
            </h3>
            
            <div style={{
              fontSize: fontSize.xs,
              color: '#666666',
              marginBottom: rem(8)
            }}>
              {format(parseISO(impact.startDate), 'dd.MM.yyyy', { locale: pl })}
              {impact.endDate && ` — ${format(parseISO(impact.endDate), 'dd.MM.yyyy', { locale: pl })}`}
              {!impact.endDate && ' — obecnie'}
            </div>
            
            <div style={{
              fontSize: fontSize.xs,
              color: '#999999',
              marginBottom: rem(12)
            }}>
              {impact.status === 'active' ? 'Aktywny' : 'Zakończony'}
            </div>
            
            <div style={{
              padding: rem(12),
              background: '#FFF5E6',
              borderRadius: rem(8),
              border: `1px solid ${getConclusionColor(impact.conclusion)}`
            }}>
              <div style={{
                fontSize: fontSize.sm,
                fontWeight: 600,
                color: getConclusionColor(impact.conclusion),
                marginBottom: rem(4)
              }}>
                {getConclusionLabel(impact.conclusion)}
              </div>
              {impact.hasEnoughData && (
                <div style={{
                  fontSize: fontSize.xs,
                  color: '#666666',
                  lineHeight: 1.4
                }}>
                  {impact.summary.substring(0, 100)}
                  {impact.summary.length > 100 && '...'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
